import React, { useState, useContext, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, APP_NAME, AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const Signup = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const logoUrl = "https://yourpersonalshopper-storage.sfo3.digitaloceanspaces.com/assets/bo_logo.webp";
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    shop_name: "",
    plan: "monthly",
  });
  const [loading, setLoading] = useState(false);

  // Update page title and handle plan query parameter
  useEffect(() => {
    document.title = `Sign Up - ${APP_NAME}`;

    // Get plan from query parameter, default to "monthly" if not present
    const planParam = searchParams.get("plan") || "monthly";
    setFormData(prev => ({ ...prev, plan: planParam }));
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/signup`, formData);
      login(response.data.token, response.data.shop);
      toast.success("Welcome! Your account is ready.");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1a1f] via-[#005f6b] to-[#0a1a1f] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4 cursor-pointer" onClick={() => navigate("/")}>
            <img src={logoUrl} alt="Logo" className="w-14 h-14 rounded-full" />
            <span className="text-2xl font-semibold text-[#d4af37]">BackOffice</span>
          </div>
          <h1 className="text-3xl font-bold text-[#f8f8f8] mb-2">Create Your Account</h1>
          <p className="text-[#f8f8f8]/70">Your Business. Streamlined. Automated. Liberated.</p>
        </div>

        <div className="bg-[#0a1a1f]/80 backdrop-blur-sm rounded-2xl border border-[#d4af37]/20 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="shop_name" className="text-[#f8f8f8]">Business Name</Label>
              <Input
                id="shop_name"
                data-testid="shop-name-input"
                type="text"
                value={formData.shop_name}
                onChange={(e) => setFormData({ ...formData, shop_name: e.target.value })}
                placeholder="Elegant Bridal Boutique"
                required
                className="mt-2 bg-[#005f6b]/20 border-[#005f6b] text-[#f8f8f8] placeholder:text-[#f8f8f8]/40 focus:border-[#00ced1] focus:ring-[#00ced1]"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-[#f8f8f8]">Email</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@business.com"
                required
                className="mt-2 bg-[#005f6b]/20 border-[#005f6b] text-[#f8f8f8] placeholder:text-[#f8f8f8]/40 focus:border-[#00ced1] focus:ring-[#00ced1]"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#f8f8f8]">Password</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                required
                className="mt-2 bg-[#005f6b]/20 border-[#005f6b] text-[#f8f8f8] placeholder:text-[#f8f8f8]/40 focus:border-[#00ced1] focus:ring-[#00ced1]"
              />
            </div>

            <Button
              data-testid="signup-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold py-6 rounded-xl text-base"
            >
              {loading ? "Creating..." : "Create Account"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-[#f8f8f8]/70">
              Already have an account?{" "}
              <Link to="/login" className="text-[#00ced1] hover:text-[#d4af37] font-medium">
                Login
              </Link>
            </p>
          </div>

          <div className="mt-6 text-center text-xs space-x-4">
            <Link to="/privacy-policy" className="text-[#00ced1] hover:text-[#d4af37] font-medium">
              Privacy Policy
            </Link>
            <Link to="/help-guide" className="text-[#00ced1] hover:text-[#d4af37] font-medium">
              Help Guide
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
